class NslFeaturedSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slider-slide]'));
    this.controls = this.querySelector('[data-slider-controls]');
    this.previousButton = this.querySelector('[data-slider-previous]');
    this.nextButton = this.querySelector('[data-slider-next]');
    this.autoplayDelay = Number(this.dataset.autoplayDelay) || 5000;
    this.manualRestartDelay = 9000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.abortController = new AbortController();
    this.isInteracting = false;
    this.isAnimating = false;
    this.logicalIndex = 0;
    this.singleStep = 0;

    if (!this.track || this.slides.length === 0) return;

    const { signal } = this.abortController;
    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
    this.track.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.track.addEventListener('wheel', () => this.pauseTemporarily(), { passive: true, signal });
    this.previousButton?.addEventListener('click', () => this.onArrowClick(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.onArrowClick(1), { signal });
    this.addEventListener('mouseenter', () => this.pause(), { signal });
    this.addEventListener('mouseleave', () => this.play(), { signal });
    this.addEventListener('focusin', () => this.pause(), { signal });
    this.addEventListener('focusout', () => window.setTimeout(() => this.play(), 0), { signal });
    this.track.addEventListener('pointerdown', () => this.startInteraction(), { signal });
    this.track.addEventListener('pointerup', () => this.endInteraction(), { signal });
    this.track.addEventListener('pointercancel', () => this.endInteraction(), { signal });
    document.addEventListener('visibilitychange', () => this.onVisibilityChange(), { signal });
    this.reducedMotion.addEventListener('change', () => this.play(), { signal });

    this.resizeObserver = new ResizeObserver(() => {
      const stableIndex = Math.min(this.logicalIndex, this.maximumIndex);
      this.cancelAnimation();
      this.measureSingleStep();
      this.logicalIndex = stableIndex;
      this.track.scrollLeft = stableIndex * this.singleStep;
      this.updateControls();
      this.play();
    });
    this.resizeObserver.observe(this.track);
    this.slides.forEach((slide) => this.resizeObserver.observe(slide));
    this.measureSingleStep();
    this.updateControls();
    this.play();
  }

  disconnectedCallback() {
    this.pause();
    this.cancelAnimation();
    window.clearTimeout(this.resumeTimer);
    this.resizeObserver?.disconnect();
    this.abortController?.abort();
  }

  get visibleSlides() {
    const step = this.measureSingleStep() || 1;
    const gap = this.computedGap;
    return Math.max(1, Math.round((this.track.clientWidth + gap) / step));
  }

  get maximumIndex() {
    return Math.max(0, this.slides.length - this.visibleSlides);
  }

  get computedGap() {
    const styles = getComputedStyle(this.track);
    return Number.parseFloat(styles.columnGap || styles.gap) || 0;
  }

  measureSingleStep() {
    const visibleSlide = this.slides.find((slide) => {
      const bounds = slide.getBoundingClientRect();
      return bounds.width > 0 && bounds.height > 0;
    });

    if (!visibleSlide) return this.singleStep;

    this.singleStep = visibleSlide.getBoundingClientRect().width + this.computedGap;
    return this.singleStep;
  }

  syncLogicalIndex() {
    const step = this.measureSingleStep();
    if (!step) return;
    this.logicalIndex = Math.min(this.maximumIndex, Math.max(0, Math.round(this.track.scrollLeft / step)));
  }

  move(direction) {
    if (this.maximumIndex === 0 || this.isAnimating) return;

    this.syncLogicalIndex();
    const normalizedDirection = direction > 0 ? 1 : -1;
    const next = this.prepareLoop(normalizedDirection);

    this.scrollToIndex(next);
  }

  onArrowClick(direction) {
    this.pauseTemporarily(this.manualRestartDelay);
    this.move(direction);
  }

  prepareLoop(direction) {
    const step = this.measureSingleStep();

    if (direction > 0 && this.logicalIndex >= this.maximumIndex) {
      const firstSlide = this.slides.shift();
      this.track.append(firstSlide);
      this.slides.push(firstSlide);
      this.logicalIndex = Math.max(0, this.maximumIndex - 1);
      this.track.scrollLeft = this.logicalIndex * step;
    } else if (direction < 0 && this.logicalIndex <= 0) {
      const lastSlide = this.slides.pop();
      this.track.prepend(lastSlide);
      this.slides.unshift(lastSlide);
      this.logicalIndex = Math.min(1, this.maximumIndex);
      this.track.scrollLeft = this.logicalIndex * step;
    }

    return this.logicalIndex + direction;
  }

  scrollToIndex(index) {
    const step = this.measureSingleStep();
    if (!step) return;

    const start = this.track.scrollLeft;
    const target = index * step;
    this.logicalIndex = index;

    if (this.reducedMotion.matches) {
      this.track.scrollLeft = target;
      return;
    }

    const duration = 420;
    const startedAt = performance.now();
    const distance = target - start;
    this.isAnimating = true;

    const animate = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.track.scrollLeft = start + distance * eased;

      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(animate);
      } else {
        this.animationFrame = null;
        this.isAnimating = false;
        this.track.scrollLeft = target;
      }
    };

    this.animationFrame = requestAnimationFrame(animate);
  }

  cancelAnimation() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    this.isAnimating = false;
  }

  onKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.pauseTemporarily(this.manualRestartDelay);
    this.move(event.key === 'ArrowRight' ? 1 : -1);
  }

  onScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      if (!this.isAnimating) this.syncLogicalIndex();
    });
  }

  startInteraction() {
    this.isInteracting = true;
    this.cancelAnimation();
    this.pause();
  }

  endInteraction() {
    this.isInteracting = false;
    this.pauseTemporarily(this.manualRestartDelay);
  }

  pauseTemporarily(delay = this.manualRestartDelay) {
    this.pause();
    window.clearTimeout(this.resumeTimer);
    this.resumeNotBefore = Date.now() + delay;
    this.resumeTimer = window.setTimeout(() => this.play(), delay);
  }

  updateControls() {
    if (this.controls) this.controls.hidden = this.maximumIndex === 0;
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.pause();
    } else {
      this.play();
    }
  }

  play() {
    this.pause();
    const remainingDelay = Math.max(0, (this.resumeNotBefore || 0) - Date.now());
    if (remainingDelay > 0) {
      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => this.play(), remainingDelay);
      return;
    }
    if (
      document.hidden ||
      this.reducedMotion.matches ||
      this.maximumIndex === 0 ||
      this.isInteracting ||
      this.matches(':hover') ||
      this.contains(document.activeElement)
    ) {
      return;
    }
    this.autoplayTimer = window.setInterval(() => this.move(1), this.autoplayDelay);
  }

  pause() {
    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }
}

if (!customElements.get('nsl-featured-slider')) {
  customElements.define('nsl-featured-slider', NslFeaturedSlider);
}
